import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useSocket } from '../../hooks/useSocket';
import { ChevronRight, ChevronLeft, MapPin, LayoutGrid, Search, Filter, Calendar, Phone, CheckCircle, Clock, ArrowRight, List, Columns, CalendarClock, X, CheckCircle2, CreditCard, RotateCcw, PhoneCall, AlertCircle, Sparkles, Bell, Flame, Webhook } from 'lucide-react';
import { createPortal } from 'react-dom';
import { TableSkeleton } from '../../Components/common/Skeleton';
import LeadKanban from './LeadKanban';
import Pagination from '../../Components/common/Pagination';
import { hasPermission } from '../../utils/permission';

const LeadList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentMenu = searchParams.get('menu') || 'leads';
  const urlPage = parseInt(searchParams.get('page')) || 1;
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    city: '',
    treatment: '',
    priority: '',
    assignedHospital: '',
    assignedCustomerCare: '',
    startDate: '',
    endDate: '',
    eligibilityMode: ''
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [hospitals, setHospitals] = useState([]);
  const [agents, setAgents] = useState([]);
  const [pagination, setPagination] = useState({
    page: urlPage,
    limit: 10,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    if (pagination.page !== urlPage) {
      setPagination(prev => ({ ...prev, page: urlPage }));
    }
  }, [urlPage]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['pipeline', 'pending', 'followup', 'lost', 'completed'].includes(tab)) {
      setActiveTab(tab);
    } else if (!tab) {
      setActiveTab('pipeline');
    }
  }, [searchParams]);

  const [pendingLeads, setPendingLeads] = useState([]);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [loadingPending, setLoadingPending] = useState(false);
  const [pendingLimit, setPendingLimit] = useState(20);
  const [lostLeads, setLostLeads] = useState([]);
  const [lostTotal, setLostTotal] = useState(0);
  const [loadingLost, setLoadingLost] = useState(false);
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'pipeline'); // 'pipeline', 'pending', or 'lost'
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'kanban'
  const [showCCReassignModal, setShowCCReassignModal] = useState(false);
  const [selectedLeadForDetails, setSelectedLeadForDetails] = useState(null);
  const [selectedLeadForCC, setSelectedLeadForCC] = useState(null);
  const [reassigning, setReassigning] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [followUpLead, setFollowUpLead] = useState(null);
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpTime, setFollowUpTime] = useState('10:00');
  const [followUpNotes, setFollowUpNotes] = useState('');
  const [schedulingFollowUp, setSchedulingFollowUp] = useState(false);
  const [showLostModal, setShowLostModal] = useState(false);
  const [leadToMarkLost, setLeadToMarkLost] = useState(null);
  const [lostReasonText, setLostReasonText] = useState('');
  const [isMarkingLost, setIsMarkingLost] = useState(false);
  const [leadToConvertWithFollowUp, setLeadToConvertWithFollowUp] = useState(null);
  const [leadToRestore, setLeadToRestore] = useState(null);
  const [followUpLeads, setFollowUpLeads] = useState([]);
  const [followUpTotal, setFollowUpTotal] = useState(0);
  const [loadingFollowUp, setLoadingFollowUp] = useState(false);
  const [followUpSubFilter, setFollowUpSubFilter] = useState('all'); // 'all', 'today', 'overdue', 'upcoming'
  const [completedLeads, setCompletedLeads] = useState([]);
  const [completedTotal, setCompletedTotal] = useState(0);
  const [loadingCompleted, setLoadingCompleted] = useState(false);

  // Date helper functions for Follow-up status
  const isFollowUpToday = (dateString) => {
    if (!dateString) return false;
    const d = new Date(dateString);
    const now = new Date();
    return (
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate()
    );
  };

  const isFollowUpOverdue = (dateString) => {
    if (!dateString) return false;
    const d = new Date(dateString);
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    return d < startOfToday;
  };

  const isFollowUpUpcoming = (dateString) => {
    if (!dateString) return false;
    const d = new Date(dateString);
    const now = new Date();
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    return d > endOfToday;
  };

  const getLocalDateString = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const { socket } = useSocket();

  useEffect(() => {
    fetchLeads();
    if (user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') {
      fetchPendingLeads();
      fetchFollowUpLeads();
      fetchLostLeads();
      fetchCompletedLeads();
    }
  }, [pagination.page, filters, viewMode, user?.role, pendingLimit, currentMenu]);

  useEffect(() => {
    if (showAdvanced || user?.role === 'admin') {
      fetchFilterOptions();
    }
  }, [showAdvanced, user?.role]);

  const fetchFilterOptions = async () => {
    try {
      const [hospRes, agentRes] = await Promise.all([
        api.get('/hospitals?limit=100'),
        api.get('/users?role=customer_care&limit=100')
      ]);
      if (hospRes.data?.success) setHospitals(hospRes.data.data.hospitals || []);
      if (agentRes.data?.success) setAgents(agentRes.data.data.users || []);
    } catch (error) {
      console.error('Failed to load filter options:', error);
    }
  };

  const handleReassignCC = async (agentId) => {
    if (!selectedLeadForCC) return;
    setReassigning(true);
    try {
      await api.patch(`/leads/${selectedLeadForCC._id}`, { assignedCustomerCare: agentId });
      toast.success('Lead reassigned successfully');
      setShowCCReassignModal(false);
      setSelectedLeadForCC(null);
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reassign lead');
    } finally {
      setReassigning(false);
    }
  };

  const handleScheduleFollowUp = async () => {
    if (!followUpLead || !followUpDate) return;
    setSchedulingFollowUp(true);
    try {
      const dateTime = followUpTime ? `${followUpDate}T${followUpTime}:00` : `${followUpDate}T10:00:00`;
      await api.post(`/leads/${followUpLead._id}/follow-up`, {
        date: dateTime,
        notes: followUpNotes || `Follow-up call scheduled for ${followUpLead.patientName}`
      });
      toast.success('Follow-up scheduled successfully');
      setShowFollowUpModal(false);
      setFollowUpLead(null);
      setFollowUpDate('');
      setFollowUpTime('10:00');
      setFollowUpNotes('');
      fetchPendingLeads();
      fetchFollowUpLeads();
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to schedule follow-up');
    } finally {
      setSchedulingFollowUp(false);
    }
  };

  const handleMarkLost = async () => {
    if (!leadToMarkLost || !lostReasonText.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    setIsMarkingLost(true);
    try {
      await api.post(`/leads/${leadToMarkLost._id}/not-interested`, { reason: lostReasonText.trim() });
      toast.success('Lead marked as lost');
      setShowLostModal(false);
      setLeadToMarkLost(null);
      setLostReasonText('');
      fetchLeads();
      if (user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') {
        fetchPendingLeads();
        fetchFollowUpLeads();
        fetchLostLeads();
      }
      if (selectedLeadForDetails?._id === leadToMarkLost._id) {
        setSelectedLeadForDetails(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update lead');
    } finally {
      setIsMarkingLost(false);
    }
  };

  // REAL-TIME AUTO-REFRESH
  useEffect(() => {
    if (!socket) return;

    const handleRealTimeUpdate = (data) => {


      if (data.entityType === 'lead' || data.action?.includes('lead') || data.action === 'created') {


        if (data.action === 'created' || data.action === 'lead_created') {
          setPagination(prev => {
            if (prev.page === 1) {
              fetchLeads();
              if (user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') {
                fetchPendingLeads();
                fetchFollowUpLeads();
                fetchLostLeads();
                fetchCompletedLeads();
              }
            }
            return { ...prev, page: 1 };
          });
        } else {
          fetchLeads();
          if (user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') {
            fetchPendingLeads();
            fetchFollowUpLeads();
            fetchLostLeads();
            fetchCompletedLeads();
          }
        }

        if (data.patientName && (data.action === 'lead_status_updated' || data.action === 'created')) {
          toast.success(`${data.action === 'created' ? 'New Registration' : 'Pipeline Move'}: ${data.patientName}`, {
            icon: data.action === 'created' ? '🆕' : '🚀',
            id: `notif-${data.leadId || Date.now()}`
          });
        }
      }
    };

    socket.on('lead_updated', handleRealTimeUpdate);
    socket.on('personal_notification', handleRealTimeUpdate);
    socket.on('system_activity', handleRealTimeUpdate);

    return () => {
      socket.off('lead_updated', handleRealTimeUpdate);
      socket.off('personal_notification', handleRealTimeUpdate);
      socket.off('system_activity', handleRealTimeUpdate);
    };
  }, [socket]);

  const fetchLeads = async () => {
    try {
      if (leads.length > 0) {
        setIsFetching(true);
      }
      const params = new URLSearchParams({
        page: viewMode === 'kanban' ? 1 : pagination.page,
        limit: viewMode === 'kanban' ? 100 : pagination.limit,
        sort: 'createdAt:desc',
        ...(filters.status && { status: filters.status }),
        ...(filters.source && { source: filters.source }),
        ...(filters.search && { search: filters.search }),
        ...(filters.city && { city: filters.city }),
        ...(filters.treatment && { treatment: filters.treatment }),
        ...(filters.priority && { priority: filters.priority }),
        ...(filters.assignedHospital && { assignedHospital: filters.assignedHospital }),
        ...(filters.assignedCustomerCare && { assignedCustomerCare: filters.assignedCustomerCare }),
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate }),
        ...(filters.eligibilityMode && { eligibilityMode: filters.eligibilityMode })
      });

      const response = await api.get(`/leads?${params}`);
      if (response.data?.success) {
        setLeads(response.data.data.leads || []);
        setPagination((prev) => ({
          ...prev,
          total: response.data.data.total || 0,
          pages: response.data.data.pages || 0
        }));
      }
    } catch (error) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  const fetchPendingLeads = async () => {
    try {
      setLoadingPending(true);
      const params = new URLSearchParams({
        registrationPending: 'true',
        limit: pendingLimit,
        sort: 'createdAt:desc',
        ...(filters.search && { search: filters.search }),
        ...(filters.startDate && { startDate: filters.startDate })
      });
      const response = await api.get(`/leads?${params}`);
      if (response.data?.success) {
        setPendingLeads(response.data.data.leads || []);
        setPendingTotal(response.data.data.total || 0);
      }
    } catch (error) {
      console.error('Failed to load pending leads:', error);
    } finally {
      setLoadingPending(false);
    }
  };

  const fetchFollowUpLeads = async () => {
    try {
      setLoadingFollowUp(true);
      const params = new URLSearchParams({
        hasFollowUp: 'true',
        limit: '100',
        sort: 'nextFollowUpDate:asc',
        ...(filters.search && { search: filters.search }),
        ...(filters.startDate && { startDate: filters.startDate })
      });
      const response = await api.get(`/leads?${params}`);
      if (response.data?.success) {
        setFollowUpLeads(response.data.data.leads || []);
        setFollowUpTotal(response.data.data.total || 0);
      }
    } catch (error) {
      console.error('Failed to load follow-up leads:', error);
    } finally {
      setLoadingFollowUp(false);
    }
  };

  const fetchLostLeads = async () => {
    try {
      setLoadingLost(true);
      if (currentMenu === 'appointments') {
        const params = new URLSearchParams({
          status: 'lost',
          limit: '50',
          ...(filters.search && { search: filters.search }),
          ...(filters.startDate && { startDate: filters.startDate })
        });
        const response = await api.get(`/surgeries?${params}`);
        if (response.data?.success) {
          const mapped = (response.data.data.surgeries || [])
            .filter(s => s.lead)
            .map(s => ({
              ...s.lead,
              updatedAt: s.updatedAt || s.createdAt,
              status: 'not_interested',
              surgeryInfo: s
            }));
          setLostLeads(mapped);
          setLostTotal(response.data.data.pagination?.total || 0);
        }
      } else {
        const params = new URLSearchParams({
          status: 'not_interested',
          pipelineChoice: 'lead_management',
          limit: '50',
          sort: 'updatedAt:desc',
          ...(filters.search && { search: filters.search }),
          ...(filters.startDate && { startDate: filters.startDate })
        });
        const response = await api.get(`/leads?${params}`);
        if (response.data?.success) {
          setLostLeads(response.data.data.leads || []);
          setLostTotal(response.data.data.total || 0);
        }
      }
    } catch (error) {
      console.error('Failed to load lost leads:', error);
    } finally {
      setLoadingLost(false);
    }
  };

  const fetchCompletedLeads = async () => {
    try {
      setLoadingCompleted(true);
      if (currentMenu === 'appointments') {
        const params = new URLSearchParams({
          status: 'closed',
          limit: '50',
          ...(filters.search && { search: filters.search }),
          ...(filters.startDate && { startDate: filters.startDate })
        });
        const response = await api.get(`/surgeries?${params}`);
        if (response.data?.success) {
          const mapped = (response.data.data.surgeries || [])
            .filter(s => s.lead)
            .map(s => ({
              ...s.lead,
              updatedAt: s.updatedAt || s.createdAt,
              status: 'completed',
              surgeryInfo: s
            }));
          setCompletedLeads(mapped);
          setCompletedTotal(response.data.data.pagination?.total || 0);
        }
      } else {
        const params = new URLSearchParams({
          status: 'completed',
          limit: '50',
          sort: 'updatedAt:desc',
          ...(filters.search && { search: filters.search }),
          ...(filters.startDate && { startDate: filters.startDate })
        });
        const response = await api.get(`/leads?${params}`);
        if (response.data?.success) {
          setCompletedLeads(response.data.data.leads || []);
          setCompletedTotal(response.data.data.total || 0);
        }
      }
    } catch (error) {
      console.error('Failed to load completed leads:', error);
    } finally {
      setLoadingCompleted(false);
    }
  };

  const handleOpenDetails = async (lead) => {
    setSelectedLeadForDetails(lead); // show initial/stale data instantly for fast UI feedback
    try {
      const response = await api.get(`/leads/${lead._id}`);
      if (response.data?.success) {
        setSelectedLeadForDetails(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch lead details from backend:', err);
    }
  };

  const handleConvertClick = async (e, lead) => {
    e.stopPropagation();
    if (lead.nextFollowUpDate) {
      e.preventDefault();
      setLeadToConvertWithFollowUp(lead);
    } else {
      setSelectedLeadForDetails(null);
      navigate(lead.source === 'DOXEZ' ? `/leads/register-cc/${lead._id}?source=web` : `/leads/register-cc/${lead._id}${lead.documents?.length > 0 ? '?submission=true' : ''}`);
    }
  };

  const handleRegisterCallClick = () => {
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('leadReg_')) {
        sessionStorage.removeItem(key);
      }
    });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPendingLimit(20);
    setSearchParams(prev => {
      prev.delete('page');
      return prev;
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setSearchParams(prev => {
      prev.set('page', newPage);
      return prev;
    });
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleStatusUpdate = async (leadId, newStatus) => {
    try {
      const response = await api.patch(`/leads/${leadId}/status`, { status: newStatus });
      if (response.data?.success) {
        return true;
      }
    } catch (error) {
      console.error('Failed to update lead status:', error);
      toast.error(error.response?.data?.message || 'Failed to transition stage');
      throw error;
    }
  };

  const resetFilters = () => {
    setFilters({
      status: '',
      source: '',
      search: '',
      city: '',
      treatment: '',
      priority: '',
      assignedHospital: '',
      assignedCustomerCare: '',
      startDate: '',
      endDate: '',
      eligibilityMode: ''
    });
    setPendingLimit(20);
    setSearchParams(prev => {
      prev.delete('page');
      return prev;
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="h-8 w-32 bg-slate-200 animate-pulse rounded-lg"></div>
        </div>
        <div className="bg-white rounded-[2rem] shadow-2xl p-8 border border-slate-100">
          <TableSkeleton rows={8} />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-4 sm:p-6 space-y-6" style={{ minHeight: '80vh' }}>
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="space-y-4">
            <div className="flex items-start justify-between w-full">
              <div>
                <h1 className="text-2xl md:text-3xl font-black text-[#143E78] tracking-tight uppercase">Lead Pipeline</h1>
                <p className="text-slate-500 text-sm font-bold mt-1">Manage and track patient inquiries, follow-ups, and treatment progress</p>
              </div>

              {activeTab !== 'pending' && (
                <div className="flex lg:hidden bg-slate-100 p-1 rounded-xl shadow-inner shrink-0 mt-1">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${viewMode === 'list' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    <List className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('kanban')}
                    className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${viewMode === 'kanban' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    <Columns className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>



            {(activeTab === 'pending' || activeTab === 'followup') && (
              <div className="flex items-center gap-3 mt-2 animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    name="search"
                    value={filters.search}
                    onChange={handleFilterChange}
                    placeholder="Quick search..."
                    className="pl-9 pr-4 py-2 bg-white border border-slate-200 focus:border-amber-500 rounded-xl outline-none text-sm font-bold text-slate-700 w-full sm:w-64 transition-all shadow-sm"
                  />
                </div>
                <input
                  type="date"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                  className="px-4 py-2 bg-white border border-slate-200 focus:border-amber-500 rounded-xl outline-none text-sm font-bold text-slate-700 transition-all shadow-sm cursor-pointer"
                />
              </div>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            {(activeTab !== 'pending' && activeTab !== 'followup') && (
              <div className="hidden lg:flex bg-slate-100 p-1 rounded-xl shadow-inner">
                <button
                  onClick={() => setViewMode('list')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${viewMode === 'list' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  <List className="w-3.5 h-3.5" /> List
                </button>
                <button
                  onClick={() => setViewMode('kanban')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${viewMode === 'kanban' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  <Columns className="w-3.5 h-3.5" /> Kanban
                </button>
              </div>
            )}
            <div className="grid grid-cols-2 sm:flex gap-2">
              {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && (
                <Link
                  to="/leads/register-cc"
                  onClick={handleRegisterCallClick}
                  className="px-3 sm:px-4 py-2.5 bg-indigo-600 text-white font-black text-[9px] sm:text-[10px] md:text-sm uppercase tracking-widest rounded-xl hover:bg-indigo-700 shadow-lg shadow-indigo-100 flex items-center justify-center gap-2 active:scale-95 transition-all whitespace-nowrap"
                >
                  <Phone className="w-3.5 h-3.5" /> Register
                </Link>
              )}
              {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head' || user?.role === 'marketing_agent' || user?.role === 'doctor' || user?.role === 'hospital') && (
                <Link
                  to="/leads/create"
                  className="px-3 sm:px-4 py-2.5 bg-white border-2 border-slate-200 text-slate-600 font-black text-[9px] sm:text-[10px] md:text-sm rounded-xl hover:bg-slate-50 transition-all shadow-sm flex items-center justify-center gap-2 active:scale-95 whitespace-nowrap"
                >
                  Create Lead
                </Link>
              )}
              {(user?.role === 'admin' || hasPermission(user, 'webhook:view') || hasPermission(user, 'webhook:manage')) && (
                <Link
                  to="/webhooks?tab=inbound"
                  className="px-3 sm:px-4 py-2.5 bg-sky-50 border border-sky-200 text-sky-700 font-bold text-[9px] sm:text-[10px] md:text-sm rounded-xl hover:bg-sky-100 transition-all shadow-sm flex items-center justify-center gap-1.5 active:scale-95 whitespace-nowrap"
                  title="Manage Inbound Webhooks for Meta Ads, Google Ads & Website Leads"
                >
                  <Webhook className="w-3.5 h-3.5 text-sky-600" /> Inbound Hooks
                </Link>
              )}
            </div>
          </div>
        </div>

        {activeTab === 'pending' && (user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && (() => {
          const filteredPending = pendingLeads.filter(lead => {
            if (lead.pipelineChoice === 'appointment' || lead.pipelineChoice === 'surgical') return false;
            let match = true;
            if (filters.search) {
              const s = filters.search.toLowerCase();
              match = match && (lead.patientName?.toLowerCase().includes(s) || lead.patientPhone?.includes(s) || lead.bookingId?.toLowerCase().includes(s));
            }
            if (filters.startDate) {
              const d = new Date(lead.createdAt).toISOString().split('T')[0];
              match = match && d === filters.startDate;
            }
            return match;
          });

          if (pendingLeads.length === 0 && !filters.search && !filters.startDate) {
            return (
              <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/40 border border-slate-100 p-12 text-center">
                <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-black text-slate-800">No Pending Logins</h3>
                <p className="text-sm font-bold text-slate-400 mt-2">All patient registrations are complete.</p>
              </div>
            );
          }

          if (filteredPending.length === 0) {
            return (
              <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/40 border border-slate-100 p-12 text-center space-y-3">
                <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center mx-auto text-indigo-500 mb-2">
                  <Search className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-black text-slate-800">No Patient Leads Found</h3>
                <p className="text-xs font-semibold text-slate-500 max-w-md mx-auto leading-relaxed">
                  We couldn't find any lead matching "<span className="text-indigo-600 font-bold">{filters.search || filters.startDate}</span>". Try again.
                </p>
                <button
                  onClick={() => {
                    setPendingLimit(20);
                    setFilters(prev => ({ ...prev, search: '', startDate: '' }));
                  }}
                  className="px-5 py-2.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 text-xs font-black rounded-xl transition-all uppercase tracking-wider inline-flex items-center gap-2 mt-2 cursor-pointer"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Clear Search & Show All Leads
                </button>
              </div>
            );
          }

          return (
            <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/40 border border-slate-100 p-6 md:p-8 space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-50 rounded-xl">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-black text-slate-800">New Leads Awaiting Contact</h3>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5 italic">Requires Call-Center Verification & Registration</p>
                  </div>
                </div>
              </div>

              <div className="space-y-8">
                {Object.entries(filteredPending
                  .reduce((acc, lead) => {
                    const dateStr = new Date(lead.createdAt).toLocaleDateString();
                    if (!acc[dateStr]) acc[dateStr] = [];
                    acc[dateStr].push(lead);
                    return acc;
                  }, {})).map(([date, leadsInDate]) => (
                    <div key={date} className="space-y-4">
                      <div className="flex items-center gap-4">
                        <div className="h-px bg-slate-200 flex-1"></div>
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest bg-slate-50 px-3 py-1 rounded-full border border-slate-200 shadow-sm">
                          {date === new Date().toLocaleDateString() ? 'Today' : date}
                        </span>
                        <div className="h-px bg-slate-200 flex-1"></div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {leadsInDate.map((lead) => (
                          <div
                            key={lead._id}
                            onClick={() => handleOpenDetails(lead)}
                            className="p-5 bg-slate-50 border border-slate-200 rounded-2xl hover:border-indigo-300 transition-all group flex flex-col cursor-pointer hover:shadow-md"
                          >
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <h4 className="font-black text-slate-800 text-sm">{lead.patientName}</h4>
                                <p className="text-[11px] font-bold text-slate-400 font-mono">{lead.patientPhone}</p>
                                {(lead.leadGeneratedByName || (['doctor', 'hospital', 'marketing_agent', 'marketing', 'partner'].includes(lead.source) && lead.createdBy)) && (
                                  <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 text-[8px] font-black uppercase tracking-widest rounded-md border border-indigo-100 mt-1 inline-block">
                                    Referred by: {(lead.leadGeneratedByName || `${lead.createdBy?.firstName || ''} ${lead.createdBy?.lastName || ''}`).trim()}
                                  </span>
                                )}
                                {lead.source === 'DOXEZ' && (
                                  <span className="px-2 py-0.5 bg-rose-50 text-rose-600 text-[8px] font-black uppercase tracking-widest rounded-md border border-rose-100 mt-1 inline-block animate-pulse">
                                    Web Inquiry
                                  </span>
                                )}
                                {lead.city && lead.city !== 'Unknown' && (
                                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                                    <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-slate-100 border border-slate-200 rounded-md">
                                      <MapPin className="w-2.5 h-2.5 text-slate-500" />
                                      <span className="text-[8px] font-black text-slate-600 uppercase tracking-tighter">{lead.city}</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-col items-end gap-1">
                                <select
                                  key={`${lead._id}-${lead.nextFollowUpDate ? 'f' : 'n'}`}
                                  className="px-2 py-1 bg-white border border-slate-200 rounded-lg text-[10px] font-black text-indigo-600 uppercase tracking-tighter shadow-sm outline-none cursor-pointer hover:bg-slate-50 transition-colors"
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={async (e) => {
                                    e.stopPropagation();
                                    const val = e.target.value;
                                    if (val === 'lost') {
                                      setLeadToMarkLost(lead);
                                      setShowLostModal(true);
                                      e.target.value = lead.nextFollowUpDate ? 'followup' : 'new';
                                    } else if (val === 'followup') {
                                      setFollowUpLead(lead);
                                      setFollowUpDate('');
                                      setFollowUpTime('10:00');
                                      setFollowUpNotes('');
                                      setShowFollowUpModal(true);
                                      e.target.value = lead.nextFollowUpDate ? 'followup' : 'new';
                                    }
                                  }}
                                  defaultValue={lead.nextFollowUpDate ? 'followup' : 'new'}
                                >
                                  <option value="new">{lead.source === 'DOXEZ' ? 'New Request' : `Step ${lead.registrationStep} / 5`}</option>
                                  <option value="followup">Follow-up</option>
                                  <option value="lost">Lost Lead</option>
                                </select>
                                {lead.documents?.length > 0 && (
                                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[8px] font-black uppercase tracking-widest rounded-md animate-pulse">
                                    Reports Received
                                  </span>
                                )}
                              </div>
                            </div>

                            {lead.nextFollowUpDate && (
                              <div className={`mt-3 flex items-center gap-2.5 px-3 py-2 rounded-xl border transition-all ${
                                isFollowUpToday(lead.nextFollowUpDate)
                                  ? 'bg-rose-50/90 border-rose-300 ring-2 ring-rose-400/20 shadow-xs'
                                  : isFollowUpOverdue(lead.nextFollowUpDate)
                                    ? 'bg-amber-50 border-amber-200'
                                    : 'bg-slate-50 border-slate-200'
                              }`}>
                                {isFollowUpToday(lead.nextFollowUpDate) ? (
                                  <div className="w-6 h-6 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center shrink-0">
                                    <PhoneCall className="w-3.5 h-3.5 animate-bounce" />
                                  </div>
                                ) : (
                                  <CalendarClock className={`w-4 h-4 shrink-0 ${isFollowUpOverdue(lead.nextFollowUpDate) ? 'text-amber-600' : 'text-slate-500'}`} />
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <p className={`text-[9px] font-black uppercase tracking-widest ${
                                      isFollowUpToday(lead.nextFollowUpDate)
                                        ? 'text-rose-600'
                                        : isFollowUpOverdue(lead.nextFollowUpDate)
                                          ? 'text-amber-700'
                                          : 'text-slate-500'
                                    }`}>
                                      {isFollowUpToday(lead.nextFollowUpDate) ? 'Follow-up Due Today' : isFollowUpOverdue(lead.nextFollowUpDate) ? 'Overdue Follow-up' : 'Follow-up Scheduled'}
                                    </p>
                                    {isFollowUpToday(lead.nextFollowUpDate) && (
                                      <span className="relative flex h-2 w-2">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-600"></span>
                                      </span>
                                    )}
                                  </div>
                                  <p className={`text-[10px] font-bold ${
                                    isFollowUpToday(lead.nextFollowUpDate)
                                      ? 'text-rose-950 font-black'
                                      : isFollowUpOverdue(lead.nextFollowUpDate)
                                        ? 'text-amber-900'
                                        : 'text-slate-700'
                                  }`}>
                                    {new Date(lead.nextFollowUpDate).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                  </p>
                                </div>
                                {isFollowUpToday(lead.nextFollowUpDate) && (
                                  <span className="text-[7px] font-black uppercase tracking-wider px-1.5 py-0.5 bg-rose-600 text-white rounded shadow-xs">
                                    Call
                                  </span>
                                )}
                              </div>
                            )}

                            <div className="flex items-center justify-between mt-auto pt-4 border-t border-slate-200/60">
                              <div className="flex flex-col">
                                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Received</span>
                                <span className="text-[10px] font-bold text-slate-600 uppercase">
                                  {new Date(lead.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={(e) => { e.stopPropagation(); setFollowUpLead(lead); setFollowUpDate(''); setFollowUpTime('10:00'); setFollowUpNotes(''); setShowFollowUpModal(true); }}
                                  className="px-3 py-2 text-[9px] font-black rounded-lg bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100 transition-all flex items-center gap-1.5"
                                  title="Schedule a follow-up call"
                                >
                                  <CalendarClock className="w-3 h-3" />
                                  {lead.nextFollowUpDate ? 'Reschedule' : 'Follow-up'}
                                </button>
                                <button
                                  onClick={(e) => handleConvertClick(e, lead)}
                                  className={`px-4 py-2 text-[10px] font-black rounded-lg transition-all flex items-center gap-2 shadow-sm ${lead.source === 'DOXEZ' ? 'bg-rose-600 text-white hover:bg-rose-700' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
                                >
                                  {lead.source === 'DOXEZ' ? 'Convert' : (lead.documents?.length > 0 ? 'Finalize' : 'Resume')} <ArrowRight className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
              </div>

              {pendingLeads.length < pendingTotal && (
                <div className="flex justify-center pt-4">
                  <button
                    onClick={() => setPendingLimit(prev => prev + 20)}
                    disabled={loadingPending}
                    className="px-6 py-2.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-black uppercase tracking-widest rounded-xl transition-all flex items-center gap-2 border border-indigo-200"
                  >
                    {loadingPending ? 'Loading...' : `Load More (${pendingTotal - pendingLeads.length} remaining)`}
                  </button>
                </div>
              )}
            </div>
          );
        })()}

        {activeTab === 'followup' && (user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && (() => {
          const baseFilteredFollowUp = followUpLeads.filter(lead => {
            if (lead.pipelineChoice === 'appointment' || lead.pipelineChoice === 'surgical') return false;
            let match = true;
            if (filters.search) {
              const s = filters.search.toLowerCase();
              match = match && (lead.patientName?.toLowerCase().includes(s) || lead.patientPhone?.includes(s) || lead.bookingId?.toLowerCase().includes(s));
            }
            if (filters.startDate) {
              if (lead.nextFollowUpDate) {
                match = match && getLocalDateString(lead.nextFollowUpDate) === filters.startDate;
              } else {
                match = false;
              }
            }
            return match;
          });

          const todayCount = baseFilteredFollowUp.filter(l => isFollowUpToday(l.nextFollowUpDate)).length;
          const overdueCount = baseFilteredFollowUp.filter(l => isFollowUpOverdue(l.nextFollowUpDate)).length;
          const upcomingCount = baseFilteredFollowUp.filter(l => isFollowUpUpcoming(l.nextFollowUpDate)).length;

          const filteredFollowUp = baseFilteredFollowUp.filter(lead => {
            if (followUpSubFilter === 'today') return isFollowUpToday(lead.nextFollowUpDate);
            if (followUpSubFilter === 'overdue') return isFollowUpOverdue(lead.nextFollowUpDate);
            if (followUpSubFilter === 'upcoming') return isFollowUpUpcoming(lead.nextFollowUpDate);
            return true;
          });

          if (followUpLeads.length === 0 && !filters.search && !filters.startDate) {
            return (
              <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/40 border border-slate-100 p-12 text-center">
                <CalendarClock className="w-12 h-12 text-indigo-400 mx-auto mb-4" />
                <h3 className="text-lg font-black text-slate-800">No Scheduled Follow-ups</h3>
                <p className="text-sm font-bold text-slate-400 mt-2">There are no pending patient follow-up calls.</p>
              </div>
            );
          }

          if (baseFilteredFollowUp.length === 0) {
            return (
              <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/40 border border-slate-100 p-12 text-center space-y-3">
                <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center mx-auto text-indigo-500 mb-2">
                  <Search className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-black text-slate-800">No Patient Leads Found</h3>
                <p className="text-xs font-semibold text-slate-500 max-w-md mx-auto leading-relaxed">
                  We couldn't find any lead matching "<span className="text-indigo-600 font-bold">{filters.search || filters.startDate}</span>". Try checking for spelling errors or clear your search filter.
                </p>
                <button
                  onClick={() => setFilters(prev => ({ ...prev, search: '', startDate: '' }))}
                  className="px-5 py-2.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 text-xs font-black rounded-xl transition-all uppercase tracking-wider inline-flex items-center gap-2 mt-2 cursor-pointer"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Clear Search & Show All Leads
                </button>
              </div>
            );
          }

          const todayKey = getLocalDateString(new Date());
          const groupedByDate = filteredFollowUp.reduce((acc, lead) => {
            const dateKey = lead.nextFollowUpDate
              ? getLocalDateString(lead.nextFollowUpDate)
              : getLocalDateString(lead.createdAt);

            const dateDisplay = lead.nextFollowUpDate
              ? new Date(lead.nextFollowUpDate).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })
              : new Date(lead.createdAt).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });

            if (!acc[dateKey]) {
              acc[dateKey] = {
                dateKey,
                dateDisplay,
                sampleDate: lead.nextFollowUpDate || lead.createdAt,
                leads: []
              };
            }
            acc[dateKey].leads.push(lead);
            return acc;
          }, {});

          const sortedGroups = Object.values(groupedByDate).sort((a, b) => {
            const isTodayA = a.dateKey === todayKey;
            const isTodayB = b.dateKey === todayKey;
            if (isTodayA && !isTodayB) return -1;
            if (!isTodayA && isTodayB) return 1;

            const isOverdueA = isFollowUpOverdue(a.sampleDate);
            const isOverdueB = isFollowUpOverdue(b.sampleDate);
            if (isOverdueA && !isOverdueB) return -1;
            if (!isOverdueA && isOverdueB) return 1;

            return a.dateKey.localeCompare(b.dateKey);
          });

          return (
            <div className="space-y-6">
              {/* 🔥 ACTION REQUIRED TODAY ALERT BANNER */}
              {todayCount > 0 && (
                <div className="relative overflow-hidden bg-gradient-to-r from-rose-600 via-red-600 to-rose-700 text-white rounded-3xl p-5 sm:p-6 shadow-xl shadow-rose-900/20 flex flex-col md:flex-row md:items-center justify-between gap-4 border border-rose-500 animate-in fade-in slide-in-from-top-2 duration-300">
                  <div className="absolute top-0 right-0 -mr-10 -mt-10 w-48 h-48 bg-white/10 rounded-full blur-2xl pointer-events-none"></div>
                  <div className="flex items-start sm:items-center gap-4 relative z-10">
                    <div className="p-3 bg-white/20 backdrop-blur-md rounded-2xl border border-white/25 shrink-0 text-white shadow-inner">
                      <PhoneCall className="w-6 h-6 animate-bounce" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-black uppercase tracking-[0.2em] bg-white/20 px-2.5 py-0.5 rounded-full backdrop-blur-sm">
                          ACTION REQUIRED TODAY
                        </span>
                        <span className="relative flex h-2.5 w-2.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white"></span>
                        </span>
                        {overdueCount > 0 && (
                          <span className="text-[10px] font-black uppercase tracking-widest bg-amber-400 text-slate-900 px-2 py-0.5 rounded-full">
                            {overdueCount} Overdue
                          </span>
                        )}
                      </div>
                      <h4 className="text-base sm:text-lg font-black tracking-tight mt-1">
                        You have {todayCount} patient follow-up{todayCount > 1 ? 's' : ''} scheduled for today ({new Date().toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })})
                      </h4>
                      <p className="text-xs font-semibold text-rose-100 mt-0.5">
                        Care coordinators and administrators can review patient files and initiate calls right now.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/40 border border-slate-100 p-6 md:p-8 space-y-6">
                {/* Header and Filter Pills */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-5">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-indigo-50 rounded-2xl text-indigo-600">
                      <CalendarClock className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-black text-slate-800">Follow-up Call List</h3>
                        {todayCount > 0 && (
                          <span className="relative flex h-2.5 w-2.5" title="Calls scheduled for today">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-600"></span>
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5 italic">
                        Patients scheduled for a callback
                      </p>
                    </div>
                  </div>

                  {/* Sub-filters (All, Due Today, Overdue, Upcoming) */}
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => setFollowUpSubFilter('all')}
                      className={`px-3.5 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer flex items-center gap-1.5 ${
                        followUpSubFilter === 'all'
                          ? 'bg-slate-900 text-white shadow-md'
                          : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                      }`}
                    >
                      <span>All</span>
                      <span className={`px-1.5 py-0.5 rounded-md text-[10px] ${followUpSubFilter === 'all' ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-700'}`}>
                        {baseFilteredFollowUp.length}
                      </span>
                    </button>

                    <button
                      onClick={() => setFollowUpSubFilter('today')}
                      className={`px-3.5 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer flex items-center gap-1.5 ${
                        followUpSubFilter === 'today'
                          ? 'bg-rose-600 text-white shadow-md shadow-rose-200'
                          : todayCount > 0
                            ? 'bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 font-black'
                            : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                      }`}
                    >
                      {todayCount > 0 && (
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-600"></span>
                        </span>
                      )}
                      <span>Due Today</span>
                      <span className={`px-1.5 py-0.5 rounded-md text-[10px] ${followUpSubFilter === 'today' ? 'bg-white/20 text-white' : todayCount > 0 ? 'bg-rose-200/80 text-rose-800 font-black' : 'bg-slate-200 text-slate-700'}`}>
                        {todayCount}
                      </span>
                    </button>

                    {overdueCount > 0 && (
                      <button
                        onClick={() => setFollowUpSubFilter('overdue')}
                        className={`px-3.5 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer flex items-center gap-1.5 ${
                          followUpSubFilter === 'overdue'
                            ? 'bg-amber-600 text-white shadow-md shadow-amber-200'
                            : 'bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200'
                        }`}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                        <span>Overdue</span>
                        <span className={`px-1.5 py-0.5 rounded-md text-[10px] ${followUpSubFilter === 'overdue' ? 'bg-white/20 text-white' : 'bg-amber-200 text-amber-900 font-black'}`}>
                          {overdueCount}
                        </span>
                      </button>
                    )}

                    <button
                      onClick={() => setFollowUpSubFilter('upcoming')}
                      className={`px-3.5 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer flex items-center gap-1.5 ${
                        followUpSubFilter === 'upcoming'
                          ? 'bg-indigo-600 text-white shadow-md shadow-indigo-200'
                          : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                      }`}
                    >
                      <span>Upcoming</span>
                      <span className={`px-1.5 py-0.5 rounded-md text-[10px] ${followUpSubFilter === 'upcoming' ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-700'}`}>
                        {upcomingCount}
                      </span>
                    </button>
                  </div>
                </div>

                {filteredFollowUp.length === 0 ? (
                  <div className="py-12 text-center space-y-3">
                    <div className="w-12 h-12 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto text-slate-400">
                      <CalendarClock className="w-6 h-6" />
                    </div>
                    <h4 className="text-base font-black text-slate-800">
                      No follow-ups found for "{followUpSubFilter === 'today' ? 'Due Today' : followUpSubFilter === 'overdue' ? 'Overdue' : 'Upcoming'}"
                    </h4>
                    <p className="text-xs font-bold text-slate-400">
                      {followUpSubFilter === 'today'
                        ? 'Great job! No patient calls are pending for today.'
                        : 'There are no leads in this category.'}
                    </p>
                    <button
                      onClick={() => setFollowUpSubFilter('all')}
                      className="px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 text-xs font-black rounded-xl uppercase tracking-wider cursor-pointer"
                    >
                      View All Follow-ups
                    </button>
                  </div>
                ) : (
                  <div className="space-y-8">
                    {sortedGroups.map((group) => {
                      const isTodayGroup = group.dateKey === todayKey;
                      const isOverdueGroup = isFollowUpOverdue(group.sampleDate);

                      return (
                        <div key={group.dateKey} className="space-y-4">
                          {/* Date Separator */}
                          <div className="flex items-center gap-4">
                            <div className={`h-px flex-1 ${isTodayGroup ? 'bg-gradient-to-r from-transparent via-rose-300 to-rose-400' : 'bg-slate-200'}`}></div>

                            {isTodayGroup ? (
                              <div className="flex items-center gap-2 bg-rose-50 text-rose-700 px-4 py-1.5 rounded-full border-2 border-rose-200 shadow-sm">
                                <span className="relative flex h-2.5 w-2.5">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-75"></span>
                                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-600"></span>
                                </span>
                                <span className="text-[11px] font-black uppercase tracking-widest">
                                  Today's Scheduled Follow-ups ({group.leads.length})
                                </span>
                              </div>
                            ) : isOverdueGroup ? (
                              <div className="flex items-center gap-2 bg-amber-50 text-amber-800 px-3.5 py-1 rounded-full border border-amber-200 shadow-xs">
                                <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                                <span className="text-[10px] font-black uppercase tracking-widest">
                                  Overdue: {group.dateDisplay} ({group.leads.length})
                                </span>
                              </div>
                            ) : (
                              <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest bg-slate-50 px-3.5 py-1 rounded-full border border-slate-200 shadow-xs">
                                {group.dateDisplay} ({group.leads.length})
                              </span>
                            )}

                            <div className={`h-px flex-1 ${isTodayGroup ? 'bg-gradient-to-l from-transparent via-rose-300 to-rose-400' : 'bg-slate-200'}`}></div>
                          </div>

                          {/* Grid of Leads */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {group.leads.map((lead) => {
                              const isTodayLead = isFollowUpToday(lead.nextFollowUpDate);
                              const isOverdueLead = isFollowUpOverdue(lead.nextFollowUpDate);

                              return (
                                <div
                                  key={lead._id}
                                  onClick={() => handleOpenDetails(lead)}
                                  className={`p-5 rounded-[2rem] transition-all duration-300 cursor-pointer group flex flex-col justify-between min-h-[230px] relative ${
                                    isTodayLead
                                      ? 'bg-gradient-to-br from-rose-50/50 via-white to-white border-2 border-rose-300 ring-4 ring-rose-500/10 shadow-lg shadow-rose-100 hover:border-rose-400 hover:shadow-xl'
                                      : isOverdueLead
                                        ? 'bg-gradient-to-br from-amber-50/30 via-white to-white border border-amber-300 shadow-sm hover:shadow-md'
                                        : 'bg-white border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200/80'
                                  }`}
                                >
                                  <div>
                                    {/* Patient Name and Status Badges */}
                                    <div className="flex justify-between items-start gap-3">
                                      <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <h4 className={`text-sm font-black uppercase tracking-tight truncate transition-colors ${isTodayLead ? 'text-slate-900 group-hover:text-rose-600' : 'text-slate-800 group-hover:text-indigo-600'}`}>
                                            {lead.patientName}
                                          </h4>
                                          {isTodayLead && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-rose-100 border border-rose-200 text-rose-700 text-[8px] font-black rounded-full uppercase tracking-wider shrink-0">
                                              <span className="relative flex h-1.5 w-1.5">
                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-75"></span>
                                                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-rose-600"></span>
                                              </span>
                                              Today
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-[10px] font-bold text-slate-400 font-mono mt-0.5">{lead.patientPhone}</p>
                                      </div>

                                      <select
                                        key={`${lead._id}-${lead.nextFollowUpDate ? 'f' : 'n'}`}
                                        className="px-2 py-1 bg-white border border-slate-200 rounded-lg text-[10px] font-black text-indigo-600 uppercase tracking-tighter shadow-sm outline-none cursor-pointer hover:bg-slate-50 transition-colors"
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={async (e) => {
                                          e.stopPropagation();
                                          const val = e.target.value;
                                          if (val === 'lost') {
                                            setLeadToMarkLost(lead);
                                            setShowLostModal(true);
                                            e.target.value = lead.nextFollowUpDate ? 'followup' : 'new';
                                          } else if (val === 'followup') {
                                            setFollowUpLead(lead);
                                            setFollowUpDate('');
                                            setFollowUpTime('10:00');
                                            setFollowUpNotes('');
                                            setShowFollowUpModal(true);
                                            e.target.value = lead.nextFollowUpDate ? 'followup' : 'new';
                                          }
                                        }}
                                        defaultValue={lead.nextFollowUpDate ? 'followup' : 'new'}
                                      >
                                        <option value="new">{lead.source === 'DOXEZ' ? 'New Request' : `Step ${lead.registrationStep} / 5`}</option>
                                        <option value="followup">Follow-up</option>
                                        <option value="lost">Lost Lead</option>
                                      </select>
                                    </div>

                                    {/* Source / City / Referred Tags */}
                                    <div className="mt-3 flex flex-wrap gap-1">
                                      {lead.leadGeneratedByName && (
                                        <span className="px-2 py-0.5 bg-slate-100 text-slate-500 text-[8px] font-black uppercase tracking-wider rounded-md border border-slate-200 mt-1 inline-block">
                                          Referred By: {lead.leadGeneratedByName}
                                        </span>
                                      )}
                                      {lead.source && (
                                        <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 text-[8px] font-black uppercase tracking-wider rounded-md border border-indigo-100 mt-1 inline-block">
                                          Source: {lead.source}
                                        </span>
                                      )}
                                      {lead.source === 'DOXEZ' && (
                                        <span className="px-2 py-0.5 bg-rose-50 text-rose-600 text-[8px] font-black uppercase tracking-widest rounded-md border border-rose-100 mt-1 inline-block animate-pulse">
                                          Web Inquiry
                                        </span>
                                      )}
                                      {lead.city && lead.city !== 'Unknown' && (
                                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                                          <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-slate-100 border border-slate-200 rounded-md">
                                            <MapPin className="w-2.5 h-2.5 text-slate-500" />
                                            <span className="text-[8px] font-black text-slate-600 uppercase tracking-tighter">{lead.city}</span>
                                          </div>
                                        </div>
                                      )}
                                    </div>

                                    {/* Scheduled Call Highlight Box */}
                                    {lead.nextFollowUpDate && (
                                      isTodayLead ? (
                                        <div className="mt-3 px-3.5 py-2.5 bg-gradient-to-r from-rose-50 to-red-50/80 border-2 border-rose-300 rounded-2xl flex items-center justify-between shadow-xs">
                                          <div className="flex items-center gap-2.5 min-w-0">
                                            <div className="w-8 h-8 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center shrink-0 shadow-xs">
                                              <PhoneCall className="w-4 h-4 animate-bounce" />
                                            </div>
                                            <div className="min-w-0">
                                              <div className="flex items-center gap-1.5">
                                                <span className="text-[9px] font-black text-rose-600 uppercase tracking-widest">Call Due Today</span>
                                                <span className="relative flex h-2 w-2">
                                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-75"></span>
                                                  <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-600"></span>
                                                </span>
                                              </div>
                                              <p className="text-xs font-black text-rose-950 truncate mt-0.5">
                                                {new Date(lead.nextFollowUpDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} • Today
                                              </p>
                                            </div>
                                          </div>
                                          <span className="shrink-0 text-[8px] font-black uppercase tracking-wider px-2.5 py-1 bg-rose-600 text-white rounded-lg shadow-xs animate-pulse">
                                            Call Now
                                          </span>
                                        </div>
                                      ) : isOverdueLead ? (
                                        <div className="mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between shadow-xs">
                                          <div className="flex items-center gap-2 min-w-0">
                                            <CalendarClock className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                                            <div className="min-w-0">
                                              <span className="text-[9px] font-black text-amber-600 uppercase tracking-widest">Overdue Follow-up</span>
                                              <p className="text-[10px] font-black text-amber-900 truncate">
                                                {new Date(lead.nextFollowUpDate).toLocaleString('en-IN', {
                                                  day: '2-digit',
                                                  month: 'short',
                                                  hour: '2-digit',
                                                  minute: '2-digit'
                                                })}
                                              </p>
                                            </div>
                                          </div>
                                          <span className="text-[8px] font-black uppercase tracking-wider px-2 py-0.5 bg-amber-200 text-amber-900 rounded-md">
                                            Overdue
                                          </span>
                                        </div>
                                      ) : (
                                        <div className="mt-3 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl flex items-center gap-2">
                                          <CalendarClock className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                                          <span className="text-[10px] font-black text-slate-700 uppercase tracking-tight">
                                            Call Scheduled: {new Date(lead.nextFollowUpDate).toLocaleString('en-IN', {
                                              day: '2-digit',
                                              month: 'short',
                                              hour: '2-digit',
                                              minute: '2-digit'
                                            })}
                                          </span>
                                        </div>
                                      )
                                    )}
                                  </div>

                                  {/* Bottom Actions */}
                                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                                    <div className="flex flex-col">
                                      <span className="text-[7px] font-black text-slate-400 uppercase tracking-widest">Received</span>
                                      <span className="text-[10px] font-bold text-slate-600 uppercase">
                                        {new Date(lead.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={(e) => { e.stopPropagation(); setFollowUpLead(lead); setFollowUpDate(''); setFollowUpTime('10:00'); setFollowUpNotes(''); setShowFollowUpModal(true); }}
                                        className="px-3 py-2 text-[9px] font-black rounded-lg bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100 transition-all flex items-center gap-1.5 cursor-pointer"
                                        title="Schedule a follow-up call"
                                      >
                                        <CalendarClock className="w-3.5 h-3.5" />
                                        Reschedule
                                      </button>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleOpenDetails(lead); }}
                                        className={`px-4 py-2 text-[10px] font-black rounded-lg transition-all flex items-center gap-2 shadow-sm cursor-pointer ${
                                          isTodayLead
                                            ? 'bg-rose-600 text-white hover:bg-rose-700 shadow-rose-200'
                                            : lead.source === 'DOXEZ'
                                              ? 'bg-rose-600 text-white hover:bg-rose-700'
                                              : 'bg-indigo-600 text-white hover:bg-indigo-700'
                                        }`}
                                      >
                                        {isTodayLead && <PhoneCall className="w-3 h-3" />}
                                        {lead.source === 'DOXEZ' ? 'Convert' : (lead.documents?.length > 0 ? 'Finalize' : isTodayLead ? 'Take Call' : 'Resume')}
                                        <ArrowRight className="w-3 h-3" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })()}
        {/* ✅ COMPLETED LEADS SECTION */}
        {activeTab === 'completed' && (user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && (
          completedLeads.length > 0 ? (
            <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/40 border border-slate-100 p-6 md:p-8 space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
              <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                <div className="w-10 h-10 bg-emerald-100 rounded-2xl flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-xl font-black text-emerald-950">Complete to Surgery</h2>
                  <p className="text-xs font-bold text-emerald-500/80 uppercase tracking-widest mt-0.5">Showing last {completedLeads.length} leads</p>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                {completedLeads.map((lead) => (
                  <div key={lead._id} className="group relative bg-white border border-emerald-100 rounded-2xl p-4 hover:shadow-md hover:border-emerald-200 transition-all duration-300 flex flex-col md:flex-row md:items-center justify-between gap-4">

                    {/* Patient Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1.5">
                        <h3 className="font-black text-slate-800 text-base group-hover:text-emerald-600 transition-colors cursor-pointer truncate" onClick={() => handleOpenDetails(lead)}>{lead.patientName}</h3>
                        <span className="text-[10px] font-black text-slate-500 font-mono bg-slate-50 px-2 py-0.5 rounded-md border border-slate-100">{lead.patientPhone}</span>
                        <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-600 text-[8px] font-black uppercase tracking-widest rounded-md border border-emerald-100 shrink-0">COMPLETED</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] font-bold text-slate-600">
                        <span className="flex items-center gap-1.5"><LayoutGrid className="w-3 h-3 text-slate-400" /> {lead.treatmentRequired || lead.disease || 'N/A'}</span>
                        <span className="flex items-center gap-1.5"><MapPin className="w-3 h-3 text-slate-400" /> {lead.city || 'N/A'}</span>
                        <span className="flex items-center gap-1.5"><Clock className="w-3 h-3 text-slate-400" /> Completed on: {new Date(lead.updatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        {user?.role === 'admin' && (
                          <span className="flex items-center gap-1 text-[10px] text-slate-500">
                            • Handled by: <span className="text-slate-800">{lead.assignedCustomerCare ? `${lead.assignedCustomerCare.firstName}` : 'N/A'}</span>
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Treatment Details / Status */}
                    <div className="flex-1 md:max-w-md w-full bg-slate-50 rounded-xl p-2.5 border border-slate-100/50">
                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Final Status Notes</p>
                      <p className="text-xs font-semibold text-slate-700 italic line-clamp-2">Patient has successfully navigated the clinical workflow to final surgery stage.</p>
                    </div>

                    {/* Action */}
                    <div className="flex-shrink-0 flex justify-end">
                      <button
                        onClick={() => handleOpenDetails(lead)}
                        className="px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 transition-colors w-full md:w-auto justify-center"
                      >
                        View Details <ArrowRight className="w-3 h-3" />
                      </button>
                    </div>

                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-12 text-center animate-in fade-in duration-300 animate-in fade-in duration-300">
              <div className="w-20 h-20 bg-emerald-50 rounded-[28px] mx-auto flex items-center justify-center mb-4">
                <CheckCircle2 className="w-10 h-10 text-emerald-500" />
              </div>
              <h3 className="text-xl font-black text-slate-800 mb-2">No Completed Cases</h3>
              <p className="text-sm font-bold text-slate-500">There are no finalized case conversions yet.</p>
            </div>
          )
        )}

        {/* 🛑 LOST LEADS SECTION */}
        {activeTab === 'lost' && (user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && (
          lostLeads.length > 0 ? (
            <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/40 border border-slate-100 p-6 md:p-8 space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
              <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                <div className="w-10 h-10 bg-rose-100 rounded-2xl flex items-center justify-center">
                  <X className="w-5 h-5 text-rose-600" />
                </div>
                <div>
                  <h2 className="text-xl font-black text-rose-950">Lost Leads / Not Interested</h2>
                  <p className="text-xs font-bold text-rose-500/80 uppercase tracking-widest mt-0.5">Showing last {lostLeads.length} leads</p>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                {lostLeads.map((lead) => (
                  <div key={lead._id} className="group relative bg-white border border-rose-100 rounded-2xl p-4 hover:shadow-md hover:border-rose-200 transition-all duration-300 flex flex-col md:flex-row md:items-center justify-between gap-4">

                    {/* Patient Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1.5">
                        <h3 className="font-black text-slate-800 text-base group-hover:text-rose-600 transition-colors cursor-pointer truncate" onClick={() => handleOpenDetails(lead)}>{lead.patientName}</h3>
                        <span className="text-[10px] font-black text-slate-500 font-mono bg-slate-50 px-2 py-0.5 rounded-md border border-slate-100">{lead.patientPhone}</span>
                        <span className="px-1.5 py-0.5 bg-rose-50 text-rose-600 text-[8px] font-black uppercase tracking-widest rounded-md border border-rose-100 shrink-0">LOST</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] font-bold text-slate-600">
                        <span className="flex items-center gap-1.5"><LayoutGrid className="w-3 h-3 text-slate-400" /> {lead.treatmentRequired || lead.disease || 'N/A'}</span>
                        <span className="flex items-center gap-1.5"><MapPin className="w-3 h-3 text-slate-400" /> {lead.city || 'N/A'}</span>
                        <span className="flex items-center gap-1.5"><Clock className="w-3 h-3 text-slate-400" /> Lost on: {new Date(lead.globallyClosedAt || lead.updatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        {user?.role === 'admin' && (
                          <span className="flex items-center gap-1 text-[10px] text-slate-500">
                            • Handled by: <span className="text-slate-800">{lead.assignedCustomerCare ? `${lead.assignedCustomerCare.firstName}` : 'N/A'}</span>
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Reason */}
                    <div className="flex-1 md:max-w-md w-full bg-slate-50 rounded-xl p-2.5 border border-slate-100/50">
                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Reason for loss</p>
                      <p className="text-xs font-semibold text-slate-700 italic line-clamp-2">"{lead.closedReason || lead.notes?.[lead.notes.length - 1]?.content || 'Not interested / No reason provided'}"</p>
                    </div>

                    {/* Action */}
                    <div className="flex-shrink-0 flex justify-end">
                      <button
                        onClick={() => handleOpenDetails(lead)}
                        className="px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 transition-colors w-full md:w-auto justify-center"
                      >
                        View Details <ArrowRight className="w-3 h-3" />
                      </button>
                    </div>

                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-12 text-center animate-in fade-in duration-300">
              <div className="w-20 h-20 bg-rose-50 rounded-[28px] mx-auto flex items-center justify-center mb-4">
                <CheckCircle2 className="w-10 h-10 text-rose-500" />
              </div>
              <h3 className="text-xl font-black text-slate-800 mb-2">No Lost Leads</h3>
              <p className="text-sm font-bold text-slate-500">There are no recently lost leads.</p>
            </div>
          )
        )}

        {activeTab === 'pipeline' && (
          <div className="space-y-6">
            <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-6 md:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Search Patient / Phone</label>
                  <input
                    type="text"
                    name="search"
                    value={filters.search}
                    onChange={handleFilterChange}
                    placeholder="e.g. Rahul Sharma..."
                    className="w-full px-4 py-3 bg-slate-50 border-2 border-transparent focus:border-[#143E78] focus:bg-white rounded-2xl outline-none font-bold transition-all text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Source of the Lead</label>
                  <select
                    name="source"
                    value={filters.source}
                    onChange={handleFilterChange}
                    className="w-full px-4 py-3 bg-slate-50 border-2 border-transparent focus:border-[#143E78] focus:bg-white rounded-2xl outline-none font-black transition-all text-sm appearance-none cursor-pointer"
                  >
                    {user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head' ? (
                      <>
                        <option value="">All Channels</option>
                        <option value="DOXEZ">Internal / DOXEZ</option>
                        <option value="doctor">Doctor Network</option>
                        <option value="hospital">Hospital Partners</option>
                        <option value="marketing_agent">Marketing / Ads</option>
                        <option value="ophe">OPHE</option>
                      </>
                    ) : user?.role === 'hospital' ? (
                      <>
                        <option value="">All My Leads</option>
                        <option value="DOXEZ">Assigned by DOXEZ</option>
                        <option value="hospital">Self Generated</option>
                      </>
                    ) : user?.role === 'doctor' ? (
                      <>
                        <option value="">All My Leads</option>
                        <option value="DOXEZ">Assigned by DOXEZ</option>
                        <option value="doctor">Self Generated</option>
                      </>
                    ) : user?.role === 'marketing_agent' ? (
                      <>
                        <option value="">All My Leads</option>
                        <option value="marketing_agent">Self Generated</option>
                      </>
                    ) : (
                      <>
                        <option value="">All Leads</option>
                        <option value={user?.role}>Self Generated</option>
                      </>
                    )}
                  </select>
                </div>
                <div className="space-y-1 sm:col-span-2 lg:col-span-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Lead Stage</label>
                  <select
                    name="status"
                    value={filters.status}
                    onChange={handleFilterChange}
                    className="w-full px-4 py-3 bg-slate-50 border-2 border-transparent focus:border-[#143E78] focus:bg-white rounded-2xl outline-none font-black transition-all text-sm text-[#143E78] appearance-none cursor-pointer"
                  >
                    <option value="">Full Pipeline View</option>
                    <option value="new">Gate 1: Intake</option>
                    <option value="screening">Gate 2: Screening</option>
                    <option value="patient_ready">Gate 3: Readiness</option>
                    <option value="doc_verification">Gate 4: Doctor Review</option>
                    <option value="clinical_approval">Gate 5: Surgeon Assignment</option>
                    <option value="hospital_assignment">Gate 6: OT Schedule</option>
                    <option value="scheduling">Gate 7: Payment Links</option>
                    <option value="payment_pending">Gate 8: Payment Pending</option>
                    <option value="paid">Gate 9: Paid / Confirmed</option>
                    <option value="surgery">Gate 10: Live Surgery</option>
                    <option value="closure">Gate 11: Post-Op Care</option>
                    <option value="payout_processing">Gate 12: Final Settlements</option>
                    <option value="completed">Gate 13: Case Finalized</option>
                    <option value="doctor_rejected">Draft: Doctor Rejected</option>
                    <option value="not_interested">Terminal: Closed / Cancelled</option>
                  </select>
                </div>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row items-center justify-between border-t border-slate-100 pt-6 gap-4">
                <button
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="flex items-center gap-2 text-[10px] font-black text-indigo-600 uppercase tracking-widest hover:text-indigo-700 transition-colors"
                >
                  <Filter className={`w-3.5 h-3.5 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
                  {showAdvanced ? 'Hide Advanced Filters' : 'Show Advanced Search'}
                </button>
                {Object.values(filters).some(v => v !== '') && (
                  <button
                    onClick={resetFilters}
                    className="text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-red-500 transition-colors"
                  >
                    Clear All Filters
                  </button>
                )}
              </div>

              {showAdvanced && (
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 animate-in fade-in slide-in-from-top-4 duration-300">
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">City / Region</label>
                    <input
                      type="text"
                      name="city"
                      value={filters.city}
                      onChange={handleFilterChange}
                      placeholder="e.g. Mumbai"
                      className="w-full px-4 py-3 bg-slate-50 border-2 border-transparent focus:border-indigo-500 rounded-2xl outline-none font-bold text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Treatment / Disease</label>
                    <input
                      type="text"
                      name="treatment"
                      value={filters.treatment}
                      onChange={handleFilterChange}
                      placeholder="e.g. Ortho"
                      className="w-full px-4 py-3 bg-slate-50 border-2 border-transparent focus:border-indigo-500 rounded-2xl outline-none font-bold text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Case Priority</label>
                    <select
                      name="priority"
                      value={filters.priority}
                      onChange={handleFilterChange}
                      className="w-full px-4 py-3 bg-slate-50 border-2 border-transparent focus:border-indigo-500 rounded-2xl outline-none font-black text-sm"
                    >
                      <option value="">All Priorities</option>
                      <option value="high">High Priority</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Assigned Hospital</label>
                    <select
                      name="assignedHospital"
                      value={filters.assignedHospital}
                      onChange={handleFilterChange}
                      className="w-full px-4 py-3 bg-slate-50 border-2 border-transparent focus:border-indigo-500 rounded-2xl outline-none font-black text-sm"
                    >
                      <option value="">Any Hospital</option>
                      {hospitals.map(h => (
                        <option key={h._id} value={h._id}>{h.hospitalName}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">CC Managed By</label>
                    <select
                      name="assignedCustomerCare"
                      value={filters.assignedCustomerCare}
                      onChange={handleFilterChange}
                      className="w-full px-4 py-3 bg-slate-50 border-2 border-transparent focus:border-indigo-500 rounded-2xl outline-none font-black text-sm"
                    >
                      <option value="">Any Agent</option>
                      {agents.map(a => (
                        <option key={a._id} value={a._id}>{a.firstName} {a.lastName}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Created From</label>
                    <input
                      type="date"
                      name="startDate"
                      value={filters.startDate}
                      onChange={handleFilterChange}
                      className="w-full px-4 py-3 bg-slate-50 border-2 border-transparent focus:border-indigo-500 rounded-2xl outline-none font-bold text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Created To</label>
                    <input
                      type="date"
                      name="endDate"
                      value={filters.endDate}
                      onChange={handleFilterChange}
                      className="w-full px-4 py-3 bg-slate-50 border-2 border-transparent focus:border-indigo-500 rounded-2xl outline-none font-bold text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Eligibility Mode</label>
                    <select
                      name="eligibilityMode"
                      value={filters.eligibilityMode}
                      onChange={handleFilterChange}
                      className="w-full px-4 py-3 bg-slate-50 border-2 border-transparent focus:border-indigo-500 rounded-2xl outline-none font-black text-sm"
                    >
                      <option value="">All Modes</option>
                      <option value="cash">Cash / Private</option>
                      <option value="insurance">Insurance</option>
                      <option value="ayushman">Ayushman Bharat</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {viewMode === 'list' ? (
              <div
                className={`bg-white rounded-[1.5rem] sm:rounded-[2rem] shadow-2xl shadow-slate-200/50 border border-slate-100 overflow-hidden transition-opacity duration-300 ${isFetching ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}
              >
                <div key={pagination.page} className="block lg:hidden divide-y divide-slate-50 animate-fadeIn">
                  {leads.length > 0 ? (
                    leads.map((lead) => (
                      <div key={lead._id} className="p-5 space-y-4 hover:bg-slate-50 transition-all">
                        <div className="flex justify-between items-start">
                          <div className="flex flex-col">
                            <span className="text-sm font-black text-slate-800">{lead.patientName}</span>
                            <span className="text-[10px] font-bold text-slate-400 font-mono mt-0.5">{lead.patientPhone}</span>
                            {(lead.leadGeneratedByName || (['doctor', 'hospital', 'marketing_agent', 'marketing', 'partner'].includes(lead.source) && lead.createdBy)) && (
                              <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 text-[8px] font-black uppercase tracking-widest rounded-md border border-indigo-100 mt-1 inline-block w-fit">
                                Referred by: {(lead.leadGeneratedByName || `${lead.createdBy?.firstName || ''} ${lead.createdBy?.lastName || ''}`).trim()}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <div className="flex gap-1">
                              <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest border ${lead.hasAyushmanCard ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                                lead.paymentType?.includes('Insurance') ? 'bg-blue-50 text-blue-700 border-blue-100' :
                                  'bg-amber-50 text-amber-700 border-amber-100'
                                }`}>
                                {lead.hasAyushmanCard ? 'Ayushman' : lead.paymentType?.includes('Insurance') ? 'Insurance' : 'Cash'}
                              </span>
                              <span className="text-[9px] font-black text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100 tracking-tighter">
                                #{lead.bookingId || lead._id?.slice(-8).toUpperCase()}
                              </span>
                            </div>
                            <div className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest border ${lead.source === 'doctor' ? 'bg-indigo-50 text-indigo-700 border-indigo-100' :
                              lead.source === 'hospital' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                                lead.source === 'ophe' ? 'bg-teal-50 text-teal-700 border-teal-100' :
                                lead.source === 'marketing_agent' ? 'bg-orange-50 text-orange-700 border-orange-100' :
                                  'bg-slate-50 text-slate-600 border-slate-100'
                              }`}>
                              {lead.source || 'INTERNAL'}
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 py-3 border-y border-slate-50">
                          <div className="space-y-1">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Treatment</p>
                            <p className="text-[10px] font-bold text-slate-700 truncate">{lead.subServiceId?.name || lead.treatmentRequired || 'General'}</p>
                          </div>
                          <div className="space-y-1">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Location</p>
                            <p className="text-[10px] font-bold text-slate-700 flex items-center gap-1">
                              <MapPin className="w-2.5 h-2.5 text-indigo-500" /> {lead.city || 'Regional'}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-1">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              <div className={`w-1.5 h-1.5 rounded-full ${lead.status === 'not_interested' ? 'bg-red-500' : 'bg-indigo-500 animate-pulse'}`}></div>
                              <span className="text-[10px] font-black text-[#143E78] uppercase tracking-tighter">
                                {lead.status?.replace(/_/g, ' ') || 'NEW'}
                              </span>
                            </div>
                            {lead.nextFollowUpDate && lead.status !== 'not_interested' && (
                              <div className={`text-[8px] font-black px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-100 flex items-center gap-1`}>
                                <Clock className="w-2.5 h-2.5" />
                                {new Date(lead.nextFollowUpDate).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                          <Link
                            to={`/leads/${lead._id}?menu=${currentMenu}`}
                            className="px-4 py-2 bg-white border border-slate-200 text-slate-600 text-[9px] font-black uppercase tracking-widest rounded-lg shadow-sm active:scale-95 transition-all"
                          >
                            View Profile
                          </Link>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-20 text-center opacity-30">
                      <LayoutGrid className="w-12 h-12 mx-auto mb-4" />
                      <p className="font-black text-xs uppercase tracking-widest">Zero Matching Leads</p>
                    </div>
                  )}
                </div>

                <div className="hidden lg:block overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="bg-[#143E78] text-white">
                        <th className="px-6 md:px-8 py-5 text-left text-[11px] font-black uppercase tracking-[0.2em] opacity-80 whitespace-nowrap">Patient & Booking</th>
                        <th className="px-6 md:px-8 py-5 text-left text-[11px] font-black uppercase tracking-[0.2em] opacity-80 whitespace-nowrap">Treatment & Location</th>
                        <th className="px-6 md:px-8 py-5 text-left text-[11px] font-black uppercase tracking-[0.2em] opacity-80 whitespace-nowrap">Ownership</th>
                        <th className="px-6 md:px-8 py-5 text-left text-[11px] font-black uppercase tracking-[0.2em] opacity-80 whitespace-nowrap">Current Status</th>
                      </tr>
                    </thead>
                    <tbody key={pagination.page} className="divide-y divide-slate-100 animate-fadeIn">
                      {leads.length > 0 ? (
                        leads.map((lead) => (
                          <tr
                            key={lead._id}
                            onClick={(e) => {
                              if (e.target.closest('button') || e.target.closest('a')) {
                                return;
                              }
                              const targetSurgeryId = lead.associatedSurgery?._id || lead._id;
                              navigate(`/surgeries/${targetSurgeryId}?leadId=${lead._id}&from=leads&origin=leads&tab=${activeTab}&menu=${currentMenu}`);
                            }}
                            className="hover:bg-blue-50/50 transition-all group cursor-pointer"
                          >
                            <td className="px-6 md:px-8 py-6">
                              <div className="flex flex-col">
                                <span className="text-base font-black text-slate-800 group-hover:text-[#143E78] transition-colors whitespace-nowrap">{lead.patientName}</span>
                                <span className="text-xs font-bold text-slate-400 font-mono mt-1">{lead.patientPhone}</span>
                                <div className="flex flex-wrap items-center gap-2 mt-2">
                                  <span className="text-[10px] font-black text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-md border border-indigo-100 tracking-tighter whitespace-nowrap">
                                    #{lead.bookingId || lead._id?.slice(-8).toUpperCase()}
                                  </span>
                                  <span className={`text-[10px] font-black px-2 py-0.5 rounded-md border tracking-tighter uppercase whitespace-nowrap ${lead.hasAyushmanCard ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                                    lead.paymentType?.includes('Insurance') ? 'bg-blue-50 text-blue-700 border-blue-100' :
                                      'bg-amber-50 text-amber-700 border-amber-100'
                                    }`}>
                                    {lead.hasAyushmanCard ? 'Ayushman' : lead.paymentType?.includes('Insurance') ? 'Insurance' : 'Cash'}
                                  </span>
                                  <span className="text-[10px] font-bold text-slate-400 flex items-center gap-1 whitespace-nowrap">
                                    <Calendar className="w-3 h-3" /> {new Date(lead.createdAt).toLocaleDateString()}
                                  </span>
                                  {lead.preferredCallTime === 'Call within 30 minutes' ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 border border-red-200 rounded-md text-[9px] font-black text-red-700 uppercase tracking-tighter whitespace-nowrap animate-pulse shadow-sm">
                                      <Phone className="w-2.5 h-2.5" />
                                      URGENT CALL: 30 MINS
                                    </span>
                                  ) : lead.preferredCallTime ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-50 border border-orange-200 rounded-md text-[9px] font-black text-orange-700 uppercase tracking-tighter whitespace-nowrap">
                                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#ea580c" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.59A2 2 0 012 1h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.91 8a16 16 0 006 6l.36-.36a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16z" /></svg>
                                      Call: {lead.preferredCallTime}
                                    </span>
                                  ) : null}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 md:px-8 py-6">
                              <div className="flex flex-col gap-2 min-w-[200px]">
                                <div className="flex flex-col">
                                  <span className="text-sm font-bold text-slate-700 leading-tight">{lead.treatmentRequired || 'General Consultation'}</span>
                                  <span className="text-[10px] font-black text-indigo-500 uppercase flex items-center gap-1 mt-1">
                                    <MapPin className="w-3 h-3" /> {lead.city || 'Regional'}
                                  </span>
                                </div>
                                {lead.assignedHospital && (
                                  <div className="bg-emerald-50/50 border border-emerald-100 p-2 rounded-xl">
                                    <p className="text-[10px] font-black text-emerald-800 uppercase leading-tight truncate">{lead.assignedHospital.hospitalName}</p>
                                    <p className="text-[9px] font-bold text-emerald-600 mt-0.5 flex items-center gap-1">
                                      <Phone className="w-2.5 h-2.5" /> {lead.assignedHospital.contactPerson?.phone || 'No Contact'}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 md:px-8 py-6">
                              <div className="flex flex-col gap-3 min-w-[150px]">
                                <div className="flex flex-col">
                                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Referred By</span>
                                  <span className="text-[11px] font-bold text-slate-700 mt-0.5 truncate max-w-[120px]">
                                    {lead.leadGeneratedByName || lead.createdBy?.firstName + ' ' + lead.createdBy?.lastName || 'Direct'}
                                  </span>
                                  <span className={`px-2 py-0.5 text-[8px] font-black rounded-md border w-fit mt-1 tracking-tighter uppercase ${lead.source === 'doctor' ? 'bg-indigo-50 text-indigo-700 border-indigo-100' :
                                    lead.source === 'hospital' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                                      lead.source === 'ophe' ? 'bg-teal-50 text-teal-700 border-teal-100' :
                                      lead.source === 'marketing_agent' ? 'bg-orange-50 text-orange-700 border-orange-100' :
                                        'bg-slate-50 text-slate-600 border-slate-100'
                                    }`}>
                                    {lead.source || 'INTERNAL'}
                                  </span>
                                  {(lead.subServiceId?.name || lead.treatmentRequired) && (
                                    <span className="px-2 py-1 text-[9px] font-black rounded-md border w-fit mt-1 tracking-tighter uppercase bg-amber-50 text-amber-700 border-amber-200 flex items-center gap-1.5 shadow-sm">
                                      <LayoutGrid className="w-3 h-3 text-amber-500" />
                                      {lead.subServiceId?.name || lead.treatmentRequired}
                                    </span>
                                  )}
                                  {lead.source === 'DOXEZ' && (
                                    <span className="px-2 py-0.5 text-[8px] font-black rounded-md border w-fit mt-1 tracking-tighter uppercase bg-rose-50 text-rose-700 border-rose-100 animate-pulse">
                                      Website Lead
                                    </span>
                                  )}
                                </div>

                                <div className="flex flex-col pt-2 border-t border-slate-100">
                                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">CC Owner</span>
                                  <button
                                    onClick={() => {
                                      if (user.role === 'admin') {
                                        setSelectedLeadForCC(lead);
                                        setShowCCReassignModal(true);
                                      }
                                    }}
                                    className={`text-[11px] font-bold mt-0.5 truncate max-w-[120px] transition-all text-left ${user.role === 'admin' ? 'text-indigo-600 hover:text-indigo-800 hover:underline cursor-pointer' : 'text-slate-500'}`}
                                  >
                                    {lead.assignedCustomerCare ? `${lead.assignedCustomerCare.firstName} ${lead.assignedCustomerCare.lastName}` : 'Unassigned'}
                                  </button>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 md:px-8 py-6">
                              <div className="flex flex-col gap-1 min-w-[150px]">
                                <div className="flex items-center gap-3">
                                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${lead.status === 'not_interested' ? 'bg-red-500' : lead.nextFollowUpDate && isFollowUpToday(lead.nextFollowUpDate) ? 'bg-rose-500 animate-ping' : lead.nextFollowUpDate && isFollowUpOverdue(lead.nextFollowUpDate) ? 'bg-amber-500 animate-pulse' : 'bg-indigo-500 animate-pulse'}`}></div>
                                  <span className={`text-[10px] md:text-xs font-black uppercase tracking-tighter whitespace-nowrap ${lead.status === 'not_interested' ? 'text-red-600' : 'text-[#143E78]'}`}>
                                    {lead.status === 'not_interested' ? 'LOST DEAL' : lead.status?.replace(/_/g, ' ') || 'NEW'}
                                  </span>
                                </div>
                                {lead.nextFollowUpDate && lead.status !== 'not_interested' && (
                                  <div className={`text-[9px] font-black px-2 py-1 rounded-lg w-fit flex items-center gap-1.5 mt-1 border whitespace-nowrap ${
                                    isFollowUpToday(lead.nextFollowUpDate)
                                      ? 'bg-rose-50 text-rose-700 border-rose-200 ring-2 ring-rose-400/20'
                                      : isFollowUpOverdue(lead.nextFollowUpDate)
                                        ? 'bg-amber-50 text-amber-800 border-amber-200'
                                        : 'bg-slate-50 text-slate-700 border-slate-200'
                                  }`}>
                                    {isFollowUpToday(lead.nextFollowUpDate) ? (
                                      <span className="relative flex h-2 w-2">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-600"></span>
                                      </span>
                                    ) : (
                                      <Calendar className="w-3 h-3" />
                                    )}
                                    <span>
                                      {isFollowUpToday(lead.nextFollowUpDate)
                                        ? `Today (${new Date(lead.nextFollowUpDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})`
                                        : new Date(lead.nextFollowUpDate).toLocaleDateString()}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="px-8 py-16 text-center">
                            <div className="flex flex-col items-center gap-3 opacity-30">
                              <LayoutGrid className="w-12 h-12 mx-auto" />
                              <p className="font-black text-lg uppercase tracking-widest">No matching deals found</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <LeadKanban leads={leads} onStatusUpdate={handleStatusUpdate} />
            )}

            {
              pagination.pages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-2">
                  <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest order-2 sm:order-1">
                    Displaying {leads.length > 0 ? (pagination.page - 1) * pagination.limit + 1 : 0}-{(pagination.page - 1) * pagination.limit + leads.length} of {pagination.total} records
                  </div>
                  <div className="order-1 sm:order-2 w-full sm:w-auto overflow-x-auto pb-2 sm:pb-0">
                    <Pagination
                      currentPage={pagination.page}
                      totalPages={pagination.pages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                </div>
              )
            }

          </div>
        )}

        {showCCReassignModal && (
          <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-slate-900/80 backdrop-blur-md">
            <div className="bg-white rounded-[2rem] w-full max-w-md mx-6 shadow-[0_32px_64px_-16px_rgba(0,0,0,0.5)] flex flex-col max-h-[85vh]">
              <div className="bg-[#143E78] rounded-t-[2rem] px-6 py-5 text-white shrink-0 relative overflow-hidden flex justify-between items-center">
                <div className="relative z-10">
                  <h3 className="text-xl font-black uppercase tracking-tighter">Reassign Agent</h3>
                  <p className="text-blue-200 text-[10px] font-bold mt-1 uppercase tracking-widest">Case: {selectedLeadForCC?.patientName}</p>
                </div>
                <button onClick={() => setShowCCReassignModal(false)} className="relative z-20 p-2 hover:bg-white/10 rounded-full transition-all">
                  <Filter className="w-5 h-5 rotate-45" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto space-y-3">
                {agents.length > 0 ? (
                  agents.map(agent => (
                    <button
                      key={agent._id}
                      disabled={reassigning}
                      onClick={() => handleReassignCC(agent._id)}
                      className={`w-full p-4 flex items-center justify-between rounded-2xl border-2 transition-all ${selectedLeadForCC?.assignedCustomerCare?._id === agent._id ? 'bg-indigo-50 border-indigo-600' : 'bg-slate-50 border-transparent hover:border-slate-200'}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-xs ${selectedLeadForCC?.assignedCustomerCare?._id === agent._id ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
                          {agent.firstName?.[0]}{agent.lastName?.[0]}
                        </div>
                        <div className="text-left">
                          <p className="font-black text-slate-800 text-sm uppercase">{agent.firstName} {agent.lastName}</p>
                          <p className="text-[10px] font-bold text-slate-400">{agent.email}</p>
                        </div>
                      </div>
                      {selectedLeadForCC?.assignedCustomerCare?._id === agent._id && (
                        <CheckCircle className="w-5 h-5 text-indigo-600" />
                      )}
                    </button>
                  ))
                ) : (
                  <div className="py-10 text-center opacity-40">
                    <p className="text-xs font-black uppercase tracking-widest">No Active Agents Found</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {showFollowUpModal && followUpLead && createPortal(
        <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-slate-900/60 backdrop-blur-md p-4">
          <div className="bg-white rounded-[2rem] w-full max-w-sm shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="bg-gradient-to-br from-amber-500 to-orange-600 px-6 py-5 text-white flex justify-between items-start">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <CalendarClock className="w-5 h-5" />
                  <h3 className="text-lg font-black uppercase tracking-tight">Schedule Follow-up</h3>
                </div>
                <p className="text-orange-100 text-[10px] font-bold uppercase tracking-widest">
                  Set callback date & time
                </p>
                <p className="text-white font-black text-sm mt-2">{followUpLead.patientName}</p>
                <p className="text-orange-200 text-[10px] font-mono">{followUpLead.patientPhone}</p>
              </div>
              <button onClick={() => setShowFollowUpModal(false)} className="p-2 hover:bg-white/20 rounded-full transition-all mt-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                  <Calendar className="w-3 h-3" /> Follow-up Date
                </label>
                <input
                  type="date"
                  value={followUpDate}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setFollowUpDate(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border-2 border-transparent focus:border-amber-400 rounded-xl outline-none font-bold text-slate-800 transition-all text-sm"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                  <Clock className="w-3 h-3" /> Call Time
                </label>
                <input
                  type="time"
                  value={followUpTime}
                  onChange={(e) => setFollowUpTime(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border-2 border-transparent focus:border-amber-400 rounded-xl outline-none font-bold text-slate-800 transition-all text-sm"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Notes (Optional)</label>
                <textarea
                  value={followUpNotes}
                  onChange={(e) => setFollowUpNotes(e.target.value)}
                  placeholder="e.g. Patient asked to call after 11 AM..."
                  rows={2}
                  className="w-full px-4 py-3 bg-slate-50 border-2 border-transparent focus:border-amber-400 rounded-xl outline-none font-medium text-slate-700 transition-all text-sm resize-none"
                />
              </div>

              {followUpDate && (
                <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
                  <CheckCircle2 className="w-4 h-4 text-amber-600 shrink-0" />
                  <p className="text-xs font-bold text-amber-800">
                    Callback set for{' '}
                    <span className="font-black">
                      {new Date(`${followUpDate}T${followUpTime || '10:00'}:00`).toLocaleString('en-IN', {
                        weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                      })}
                    </span>
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => setShowFollowUpModal(false)}
                  className="flex-1 py-3 border-2 border-slate-200 text-slate-500 font-black text-[10px] uppercase tracking-widest rounded-xl hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleScheduleFollowUp}
                  disabled={!followUpDate || schedulingFollowUp}
                  className="flex-1 py-3 bg-amber-500 text-white font-black text-[10px] uppercase tracking-widest rounded-xl hover:bg-amber-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-amber-200 flex items-center justify-center gap-2"
                >
                  {schedulingFollowUp ? (
                    <><div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</>
                  ) : (
                    <><CalendarClock className="w-3.5 h-3.5" /> Confirm</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {selectedLeadForDetails && createPortal(
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex justify-center items-center p-4">
          <div className="bg-white rounded-[2rem] w-full max-w-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 border border-slate-100">
            <div className="bg-gradient-to-r from-[#143E78] to-indigo-800 p-6 text-white flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-md">
                  <LayoutGrid className="w-5 h-5 text-indigo-100" />
                </div>
                <div>
                  <h2 className="text-sm font-black uppercase tracking-widest text-indigo-50">Patient File</h2>
                  <p className="text-[10px] font-bold text-indigo-200 mt-0.5">{selectedLeadForDetails.bookingId || selectedLeadForDetails._id?.slice(-8).toUpperCase()}</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedLeadForDetails(null)}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 md:p-8 space-y-6">
              <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                <div>
                  <h3 className="text-xl font-black text-slate-800">{selectedLeadForDetails.patientName}</h3>
                  <p className="text-sm font-bold text-slate-500 font-mono mt-1">{selectedLeadForDetails.patientPhone}</p>
                </div>
                <select
                  key={`${selectedLeadForDetails._id}-${selectedLeadForDetails.nextFollowUpDate ? 'f' : 'n'}`}
                  className="px-3 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-[10px] font-black uppercase tracking-widest shadow-sm outline-none cursor-pointer hover:bg-amber-100 transition-colors"
                  onChange={async (e) => {
                    const val = e.target.value;
                    if (val === 'lost') {
                      setLeadToMarkLost(selectedLeadForDetails);
                      setShowLostModal(true);
                      e.target.value = selectedLeadForDetails.status === 'not_interested' ? 'not_interested' : selectedLeadForDetails.nextFollowUpDate ? 'followup' : 'new';
                    } else if (val === 'followup') {
                      setFollowUpLead(selectedLeadForDetails);
                      setFollowUpDate('');
                      setFollowUpTime('10:00');
                      setFollowUpNotes('');
                      setShowFollowUpModal(true);
                      setSelectedLeadForDetails(null);
                    } else if (val === 'restore') {
                      setLeadToRestore(selectedLeadForDetails);
                      e.target.value = 'not_interested';
                    }
                  }}
                  defaultValue={selectedLeadForDetails.status === 'not_interested' ? 'not_interested' : selectedLeadForDetails.nextFollowUpDate ? 'followup' : 'new'}
                >
                  <option value={selectedLeadForDetails.status === 'not_interested' ? 'not_interested' : 'new'}>
                    {selectedLeadForDetails.status === 'not_interested'
                      ? 'Closed / Not Interested'
                      : selectedLeadForDetails.registrationComplete === false
                        ? (selectedLeadForDetails.source === 'DOXEZ' ? 'New Request' : `Step ${selectedLeadForDetails.registrationStep || 1} / 5`)
                        : (selectedLeadForDetails.status?.replace(/_/g, ' ') || 'NEW')
                    }
                  </option>
                  {selectedLeadForDetails.status === 'not_interested' && <option value="restore">Restore as New</option>}
                  {selectedLeadForDetails.status !== 'not_interested' && <option value="followup">Follow-up</option>}
                  {!['closure', 'payout_processing', 'completed', 'not_interested', 'surgery'].includes(selectedLeadForDetails.status) && <option value="lost">Lost Lead</option>}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-y-6 gap-x-4">
                <div>
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><List className="w-3 h-3" /> Treatment / Disease</p>
                  <p className="text-sm font-bold text-slate-700">{selectedLeadForDetails.subServiceId?.name || selectedLeadForDetails.treatmentRequired || 'Not Specified'}</p>
                </div>
                <div>
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><MapPin className="w-3 h-3" /> Location</p>
                  <p className="text-sm font-bold text-slate-700">
                    {selectedLeadForDetails.city && selectedLeadForDetails.city !== 'Unknown'
                      ? selectedLeadForDetails.city
                      : (selectedLeadForDetails.location && selectedLeadForDetails.location !== 'Unknown'
                        ? selectedLeadForDetails.location
                        : 'Not Specified')}
                  </p>
                </div>
                <div className="space-y-1 bg-slate-50 p-3 rounded-xl border border-slate-100">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><Phone className="w-3 h-3" /> Preferred Call Time</p>
                  {selectedLeadForDetails.preferredCallTime === 'Call within 30 minutes' ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-50 text-[10px] font-black text-red-700 uppercase tracking-tighter border border-red-200 rounded-lg animate-pulse shadow-sm">
                      <Phone className="w-3 h-3 text-red-600" /> URGENT CALL: 30 MINS
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-orange-50 text-[10px] font-black text-orange-700 uppercase tracking-tighter border border-orange-200 rounded-lg">
                      <div className="w-1.5 h-1.5 rounded-full bg-orange-500"></div>
                      {selectedLeadForDetails.preferredCallTime || 'ASAP'}
                    </span>
                  )}
                </div>
                <div>
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><Search className="w-3 h-3" /> Source</p>
                  <span className={`px-2 py-1 text-[10px] font-black uppercase tracking-widest rounded-lg border ${selectedLeadForDetails.source === 'DOXEZ' ? 'bg-rose-50 text-rose-600 border-rose-100' : 'bg-indigo-50 text-indigo-600 border-indigo-100'}`}>
                    {selectedLeadForDetails.source === 'DOXEZ' ? 'Web Inquiry' : selectedLeadForDetails.source || 'General'}
                  </span>
                </div>
                <div>
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><CreditCard className="w-3 h-3" /> Ayushman Card</p>
                  <span className={`px-2 py-1 text-[10px] font-black uppercase tracking-widest rounded-lg border ${selectedLeadForDetails.hasAyushmanCard ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-slate-50 text-slate-600 border-slate-200'}`} style={{ display: 'inline-block', textTransform: 'none' }}>
                    {selectedLeadForDetails.hasAyushmanCard ? 'Yes, I have an Ayushman Card' : "No, I don't have one"}
                  </span>
                </div>
                {selectedLeadForDetails.nextFollowUpDate && selectedLeadForDetails.status !== 'not_interested' && (
                  <div className="col-span-2 bg-amber-50 p-3 rounded-xl border border-amber-200">
                    <p className="text-[9px] font-black text-amber-600 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                      <CalendarClock className="w-3.5 h-3.5 text-amber-500" /> Follow-up Scheduled
                    </p>
                    <p className="text-sm font-bold text-amber-800">
                      {new Date(selectedLeadForDetails.nextFollowUpDate).toLocaleString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                )}
                <div className="col-span-2">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><CalendarClock className="w-3 h-3" /> Received On</p>
                  <p className="text-sm font-bold text-slate-700 bg-slate-50 px-3 py-2 rounded-xl border border-slate-100">
                    {new Date(selectedLeadForDetails.createdAt).toLocaleString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6 bg-slate-50 border-t border-slate-100 flex justify-end gap-3 rounded-b-[2rem]">
              <button
                onClick={() => setSelectedLeadForDetails(null)}
                className="px-6 py-2.5 bg-white border border-slate-200 text-slate-600 font-black text-xs uppercase tracking-widest rounded-xl hover:bg-slate-50 transition-all shadow-sm"
              >
                Close
              </button>
              {selectedLeadForDetails.status === 'not_interested' ? (
                <button
                  disabled
                  className="px-6 py-2.5 bg-slate-100 text-slate-400 font-black text-xs uppercase tracking-widest rounded-xl cursor-not-allowed border border-slate-200"
                  title="Change status from 'Not Interested' to convert this lead"
                >
                  Lost Lead (Cannot Convert)
                </button>
              ) : (
                <button
                  onClick={(e) => handleConvertClick(e, selectedLeadForDetails)}
                  className="px-6 py-2.5 bg-[#143E78] text-white font-black text-xs uppercase tracking-widest rounded-xl hover:bg-indigo-900 shadow-lg shadow-indigo-200 transition-all flex items-center gap-2"
                >
                  {selectedLeadForDetails.source === 'DOXEZ' ? 'Convert Lead' : 'Process File'} <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {showLostModal && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl sm:rounded-[32px] w-full max-w-md shadow-2xl overflow-hidden border border-slate-100 flex flex-col max-h-[90vh]">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-black text-slate-800">Lost Lead Reason</h3>
                <p className="text-sm font-bold text-slate-500 mt-1">Please specify why this lead was lost</p>
              </div>
              <button onClick={() => { setShowLostModal(false); setLeadToMarkLost(null); setLostReasonText(''); }} className="p-2 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <div className="p-6">
              <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Reason</label>
              <textarea
                value={lostReasonText}
                onChange={(e) => setLostReasonText(e.target.value)}
                placeholder="e.g. Not interested, Too expensive, Found another doctor..."
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#143E78]/20 focus:border-[#143E78] transition-all resize-none h-28"
              />
            </div>
            <div className="p-6 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
              <button
                onClick={() => { setShowLostModal(false); setLeadToMarkLost(null); setLostReasonText(''); }}
                className="px-5 py-2.5 text-sm font-black text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 rounded-xl transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkLost}
                disabled={isMarkingLost || !lostReasonText.trim()}
                className="px-5 py-2.5 bg-rose-600 text-white text-sm font-black rounded-xl hover:bg-rose-700 hover:shadow-lg hover:shadow-rose-600/20 transition-all active:scale-95 disabled:opacity-50 disabled:pointer-events-none"
              >
                {isMarkingLost ? 'Saving...' : 'Mark as Lost'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {leadToConvertWithFollowUp && createPortal(
        <div className="fixed inset-0 z-[999999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden border border-slate-100 flex flex-col transform transition-all duration-300 animate-in zoom-in-95">
            <div className="bg-gradient-to-br from-[#143E78] to-indigo-800 p-6 text-white flex items-center gap-4 shrink-0">
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-md">
                <CalendarClock className="w-5 h-5 text-indigo-100" />
              </div>
              <div>
                <h3 className="text-lg font-black uppercase tracking-tight">Pending Follow-up Call</h3>
                <p className="text-indigo-200 text-[10px] font-bold uppercase tracking-widest mt-0.5">Resolve active callback schedule</p>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm font-semibold text-slate-600 leading-relaxed">
                This patient has a pending follow-up call scheduled. If you have already spoken to them, click <strong>"Yes, Complete & Register"</strong> to mark the callback as resolved and open the registration form.
              </p>
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 flex flex-col gap-2">
                <div className="flex justify-between items-center text-xs">
                  <span className="font-bold text-slate-400 uppercase tracking-wider">Patient</span>
                  <span className="font-black text-slate-800 uppercase">{leadToConvertWithFollowUp.patientName}</span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="font-bold text-slate-400 uppercase tracking-wider">Phone</span>
                  <span className="font-mono font-bold text-slate-600">{leadToConvertWithFollowUp.patientPhone}</span>
                </div>
                {leadToConvertWithFollowUp.nextFollowUpDate && (
                  <div className="flex justify-between items-start text-xs pt-2 border-t border-slate-200/60 mt-1">
                    <span className="font-bold text-amber-600 uppercase tracking-wider">Scheduled Date</span>
                    <span className="font-bold text-amber-800 text-right">
                      {new Date(leadToConvertWithFollowUp.nextFollowUpDate).toLocaleString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 bg-slate-50 border-t border-slate-100 flex justify-end gap-3 rounded-b-[2rem]">
              <button
                onClick={() => setLeadToConvertWithFollowUp(null)}
                className="px-5 py-2.5 text-xs font-black uppercase tracking-widest text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 rounded-xl transition-all"
              >
                No, Go Back
              </button>
              <button
                onClick={async () => {
                  const lead = leadToConvertWithFollowUp;
                  setLeadToConvertWithFollowUp(null);
                  try {
                    await api.post(`/leads/${lead._id}/follow-up-action`, { action: 'ready' });
                    toast.success('Follow-up completed successfully!');
                  } catch (err) {
                    console.error('Failed to complete follow-up in backend:', err);
                  }
                  fetchLeads();
                  if (user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') {
                    fetchPendingLeads();
                    fetchFollowUpLeads();
                    fetchLostLeads();
                  }
                  setSelectedLeadForDetails(null);
                  navigate(lead.source === 'DOXEZ' ? `/leads/register-cc/${lead._id}?source=web` : `/leads/register-cc/${lead._id}${lead.documents?.length > 0 ? '?submission=true' : ''}`);
                }}
                className="px-6 py-2.5 bg-[#143E78] text-white text-xs font-black uppercase tracking-widest rounded-xl hover:bg-indigo-900 shadow-lg shadow-indigo-100 transition-all active:scale-95 flex items-center gap-2"
              >
                Yes, Complete & Register <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {leadToRestore && createPortal(
        <div className="fixed inset-0 z-[999999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden border border-slate-100 flex flex-col transform transition-all duration-300 animate-in zoom-in-95">
            <div className="bg-gradient-to-br from-[#143E78] to-indigo-800 p-6 text-white flex items-center gap-4 shrink-0">
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-md">
                <CheckCircle2 className="w-5 h-5 text-indigo-100" />
              </div>
              <div>
                <h3 className="text-lg font-black uppercase tracking-tight">Restore Lead</h3>
                <p className="text-indigo-200 text-[10px] font-bold uppercase tracking-widest mt-0.5">Reactivate lost lead</p>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm font-semibold text-slate-600 leading-relaxed">
                Are you sure you want to restore this patient back to active status? This will move them out of "Lost Leads" and back to the active pipeline.
              </p>
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 flex flex-col gap-2">
                <div className="flex justify-between items-center text-xs">
                  <span className="font-bold text-slate-400 uppercase tracking-wider">Patient</span>
                  <span className="font-black text-slate-800 uppercase">{leadToRestore.patientName}</span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="font-bold text-slate-400 uppercase tracking-wider">Phone</span>
                  <span className="font-mono font-bold text-slate-600">{leadToRestore.patientPhone}</span>
                </div>
              </div>
            </div>

            <div className="p-6 bg-slate-50 border-t border-slate-100 flex justify-end gap-3 rounded-b-[2rem]">
              <button
                onClick={() => setLeadToRestore(null)}
                className="px-5 py-2.5 text-xs font-black uppercase tracking-widest text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 rounded-xl transition-all"
              >
                No, Keep Closed
              </button>
              <button
                onClick={async () => {
                  const lead = leadToRestore;
                  setLeadToRestore(null);
                  try {
                    await api.patch(`/leads/${lead._id}/status`, { status: 'new' });
                    toast.success('Lead restored successfully!');
                    setSelectedLeadForDetails(prev => {
                      if (prev && prev._id === lead._id) {
                        return { ...prev, status: 'new' };
                      }
                      return prev;
                    });
                    fetchLeads();
                    if (user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') {
                      fetchPendingLeads();
                      fetchFollowUpLeads();
                      fetchLostLeads();
                    }
                  } catch (err) {
                    toast.error(err.response?.data?.message || 'Failed to restore lead');
                  }
                }}
                className="px-6 py-2.5 bg-[#143E78] text-white text-xs font-black uppercase tracking-widest rounded-xl hover:bg-indigo-900 shadow-lg shadow-indigo-100 transition-all active:scale-95 flex items-center gap-2"
              >
                Yes, Restore Lead <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default LeadList;